import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from "../api";
import { 
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Code as WebIcon,
  PhoneAndroid as MobileIcon,
  Computer as SupportIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Web as PortfolioIcon
} from '@mui/icons-material';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState('all');
  const theme = useTheme();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // const response = await axios.get('http://localhost:5001/api/projects');
        // const response = await axios.get('/api/projects');
        const response = await api.get('/api/projects');
        setProjects(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const getProjectIcon = (projectType) => {
    switch(projectType) {
      case 'web': return <WebIcon fontSize="large" />;
      case 'mobile': return <MobileIcon fontSize="large" />;
      case 'analytics': return <AnalyticsIcon fontSize="large" />;
      case 'security': return <SecurityIcon fontSize="large" />;
      default: return <PortfolioIcon fontSize="large" />;
    }
  };

  const getProjectColor = (index) => {
    const colors = ['primary', 'secondary', 'info', 'success', 'warning', 'error'];
    return colors[index % colors.length];
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
      <Typography>Loading projects...</Typography>
    </Container>
  );
  
  if (error) return (
    <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
      <Typography color="error">Error: {error}</Typography>
    </Container>
  );

  const filteredProjects = tabValue === 'all' 
    ? projects 
    : projects.filter(project => project.status === tabValue);

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography 
        variant="h2" 
        component="h1" 
        gutterBottom 
        align="center"
        sx={{ 
          mb: 6,
          fontWeight: 700,
          color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark
        }}
      >
        Our Projects
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          centered
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
            }
          }}
        >
          <Tab label="All Projects" value="all" />
          <Tab label="Ongoing" value="ongoing" />
          <Tab label="Completed" value="completed" />
        </Tabs>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        {filteredProjects.map((project, index) => (
          <Grid item xs={12} sm={6} md={4} lg={4} key={project._id} sx={{ display: 'flex' }}>
            <Card sx={{ 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}>
              <CardContent sx={{ 
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                py: 4,
                px: 3
              }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: theme.palette[getProjectColor(index)].main,
                    color: theme.palette[getProjectColor(index)].contrastText,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3
                  }}
                >
                  {getProjectIcon(project.type)}
                </Box>
                
                <Typography 
                  variant="h5" 
                  component="h3" 
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  {project.name}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ mb: 3, flexGrow: 1 }}
                >
                  {project.description}
                </Typography>
                
                <Chip 
                  label={project.status === 'ongoing' ? 'In Progress' : 'Completed'} 
                  color={getProjectColor(index)} 
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Projects;